import { Card } from 'antd';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { readableDate, type Paginated, type PolicyVersionDto } from '@pams/shared';
import { StatusDot } from '@/components/StatusDot';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [PRE-P6 §3b] EVERY VERSION THIS POLICY HAS EVER HAD, EACH OPENABLE READ-ONLY.
 *
 *  ⚠️ THE API HAS ANSWERED THIS SINCE [P2] AND NO CLIENT EVER ASKED. `GET
 *  /policies/:id/versions` is paginated, sortable and unfiltered by currency —
 *  `requireVersion` has no status predicate, so any historical version has always been
 *  readable at its own URL. `PolicyBuilder` could only ever open `draftVersionId ??
 *  currentVersionId`, so once v2 existed there was NO WAY to reach v1 from the app, and
 *  [LIFECYCLE] §8c/§8d left that gap open.
 *
 *  It matters more than a history usually does here, because a published version is FROZEN
 *  and orders point at it for ever: "what did this policy look like when that PA was
 *  raised?" is a question about a live operational record, not an audit curiosity.
 *
 *  ⚠️ THE VERSION NUMBER IS A REAL `<a>`. CLAUDE.md §8's `NameCell` rule — the first cell
 *  opens the row, and anything that navigates renders an anchor so ⌘-click and copy-link
 *  work. It is a QUERY PARAMETER on this same route rather than a new one, so the policy
 *  stays loaded and the back button lands on the version the operator came from.
 *
 *  ⚠️ AND IT IS BOUNDED, NOT PAGED. Versions accumulate with EDITS to one policy rather
 *  than with anything that grows without limit, and a page-two of them is not a coherent
 *  answer to "what has this policy been" — CLAUDE.md §7's stated exception, the same one
 *  `GET /officers/:id/postings` takes. If a policy ever passes the cap the panel says so
 *  rather than silently showing the newest few.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** Newest first, and enough of them that the count is the operator's problem, not ours. */
const HISTORY_LIMIT = 100;

export type VersionHistoryProps = {
  policyId: string;
  /** The version currently on screen, so the row for it is marked rather than linked. */
  viewingVersionId: string | null;
  /** The one `PolicyBuilder` would open by default — the draft, or the published current. */
  editableVersionId: string | null;
};

export const VersionHistory = ({ policyId, viewingVersionId, editableVersionId }: VersionHistoryProps) => {
  const history = useQuery({
    queryKey: queryKeys.policies.history(policyId),
    queryFn: ({ signal }) =>
      api.get<Paginated<PolicyVersionDto>>(`/policies/${policyId}/versions`, {
        query: { page: 1, limit: HISTORY_LIMIT, sortBy: 'versionNumber', sortOrder: 'desc' },
        signal,
      }),
    enabled: policyId !== '',
  });

  const rows = history.data?.items ?? [];
  const total = history.data?.total ?? 0;

  return (
    <Card size="small" title="Version history" data-testid="version-history">
      {history.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">This policy has no versions yet.</p>
      ) : (
        /* A plain table, as `RequirementPreview` uses — an AntD `Table` brings column
           machinery, a `Dropdown` the overlay census would have to account for, and a
           loading overlay, for four columns and a handful of rows. */
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="py-1.5 pr-3 font-medium">
                  Version
                </th>
                <th scope="col" className="py-1.5 pr-3 font-medium">
                  Status
                </th>
                <th scope="col" className="py-1.5 pr-3 font-medium">
                  Published
                </th>
                <th scope="col" className="py-1.5 font-medium">
                  By
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const label = `v${String(row.versionNumber)}`;
                const isViewing = row.id === viewingVersionId;

                return (
                  <tr key={row.id} className="border-b border-border last:border-0" data-testid="version-history-row">
                    <td className="py-1.5 pr-3">
                      {isViewing ? (
                        /* ⚠️ THE ROW YOU ARE ON IS NOT A LINK TO ITSELF. A link that goes
                           nowhere is the `+N` defect [D4.3] recorded, one control over. */
                        <span className="font-medium" aria-current="true">
                          {label}
                        </span>
                      ) : (
                        <Link
                          className="font-medium text-link underline-offset-2 hover:underline"
                          /* The editable version is the page's DEFAULT, so it is reached by
                             dropping the parameter rather than by naming it — one URL for
                             one screen, and no `?version=` left behind after an edit. */
                          to={
                            row.id === editableVersionId
                              ? `/policies/${policyId}`
                              : `/policies/${policyId}?version=${row.id}`
                          }
                        >
                          {label}
                        </Link>
                      )}
                    </td>
                    <td className="py-1.5 pr-3">
                      <StatusDot
                        tone={row.status === 'PUBLISHED' ? 'success' : 'warning'}
                        label={row.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                      />
                    </td>
                    <td className={cn('py-1.5 pr-3', !row.publishedAt && 'text-muted-foreground')}>
                      {/* IST, like every date this app prints. A draft has none. */}
                      {row.publishedAt ? (readableDate(row.publishedAt) ?? '—') : '—'}
                    </td>
                    <td className={cn('py-1.5', !row.publishedByName && 'text-muted-foreground')}>
                      {/* ⚠️ NEVER THE ObjectId — divergence #84. `publishedByName` is resolved
                          server-side in the same batched read as the column summaries. */}
                      {row.publishedByName ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {total > rows.length && (
            <p className="mt-2 text-xs text-muted-foreground">
              Showing the {String(rows.length)} most recent of {String(total)} versions.
            </p>
          )}
        </div>
      )}
    </Card>
  );
};
